import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = ['dialog'];
  static values = {
    open: Boolean,
    trackJobProgress: Boolean,
  };

  connect() {
    if (this.openValue) this.open();

    this.boundBeforeCache = this.beforeCache.bind(this);
    this.boundOpenById = this.openById.bind(this);

    document.addEventListener('turbo:before-cache', this.boundBeforeCache);
    document.addEventListener('modal:open', this.boundOpenById);

    if (this.trackJobProgressValue) {
      this.boundBeforeStreamRender = this.beforeStreamRender.bind(this);
      document.addEventListener('turbo:before-stream-render', this.boundBeforeStreamRender);
    }
  }

  disconnect() {
    document.removeEventListener('turbo:before-cache', this.boundBeforeCache);
    document.removeEventListener('modal:open', this.boundOpenById);
    if (this.boundBeforeStreamRender) {
      document.removeEventListener('turbo:before-stream-render', this.boundBeforeStreamRender);
    }
  }

  open() {
    this.dialogTarget.showModal();
    this.dialogTarget.querySelector('[autofocus]')?.focus();
  }

  openById(event) {
    const requestedId = event.detail?.id;
    if (!requestedId) return;
    if (this.element.id === requestedId) {
      this.open();
      return;
    }

    const container = this.element.querySelector(`#${CSS.escape(requestedId)}`);
    if (!container) return;

    const dialog = container.querySelector('dialog');
    if (!dialog) return;

    dialog.showModal();
    dialog.querySelector('[autofocus]')?.focus();
  }

  close() {
    if (this.trackJobProgressValue) {
      this.clearJobProgressContainer();
    }

    this.dialogTarget.setAttribute('closing', '');

    Promise.all(this.dialogTarget.getAnimations().map((animation) => animation.finished)).then(
      () => {
        this.dialogTarget.removeAttribute('closing');
        this.dialogTarget.close();
      }
    );
  }

  handleSubmitEnd(event) {
    if (event.detail.success) this.close();
  }

  backdropClose(event) {
    if (event.target.nodeName !== 'DIALOG') return;
    if (window.getSelection().toString().length > 0) return;

    this.close();
  }

  show() {
    this.dialogTarget.show();
  }

  hide() {
    this.close();
  }

  beforeCache() {
    if (this.trackJobProgressValue) {
      this.clearJobProgressContainer();
      this.dismissDialogImmediately();
      return;
    }

    if (this.hasDialogTarget) {
      this.close();
    }
  }

  dismissDialogImmediately() {
    if (!this.hasDialogTarget) return;

    this.dialogTarget.removeAttribute('closing');
    if (this.dialogTarget.open) this.dialogTarget.close();
  }

  clearJobProgressContainer() {
    if (!this.trackJobProgressValue) return;

    const container = document.getElementById('job_progress_container');
    const stream = document.getElementById('job_progress_stream');
    if (!container && !stream) return;

    if (container) container.innerHTML = '';
    if (stream) stream.innerHTML = '';
  }

  beforeStreamRender(event) {
    if (event?.target?.getAttribute?.('target') !== 'job_progress_container') return;

    if (!this.isDialogOpen()) {
      event.preventDefault();
      return;
    }

    const incoming = this.extractJobTrackingMeta(event.target, 'template');
    const current = this.extractJobTrackingMeta(
      document.getElementById('job_progress_container'),
      'query'
    );
    if (incoming && current && this.isStaleJobTrackingStream(incoming, current)) {
      event.preventDefault();
    }
  }

  isDialogOpen() {
    return this.hasDialogTarget && this.dialogTarget.open;
  }

  extractJobTrackingMeta(source, mode) {
    const root =
      mode === 'template'
        ? source?.templateElement?.content?.querySelector?.('[data-job-tracking-id]') ||
          source?.querySelector?.('template')?.content?.querySelector?.('[data-job-tracking-id]')
        : source?.querySelector?.('[data-job-tracking-id]');
    if (!root) return null;

    return {
      id: root.dataset.jobTrackingId || '',
      updatedAt: root.dataset.jobTrackingUpdatedAt || '',
      state: root.dataset.jobTrackingState || '',
      currentProgress: this.parseJobProgress(root.dataset.jobTrackingCurrentProgress),
      totalProgress: this.parseJobProgress(root.dataset.jobTrackingTotalProgress),
    };
  }

  isStaleJobTrackingStream(incoming, current) {
    const incomingTs = Date.parse(incoming.updatedAt);
    const currentTs = Date.parse(current.updatedAt);

    if (!Number.isNaN(incomingTs) && !Number.isNaN(currentTs)) {
      if (incomingTs < currentTs) return true;
      if (incomingTs > currentTs) return false;
    }

    const incomingStateRank = this.jobTrackingStateRank(incoming.state);
    const currentStateRank = this.jobTrackingStateRank(current.state);
    if (incomingStateRank < currentStateRank) return true;
    if (incomingStateRank > currentStateRank) return false;

    if (!Number.isNaN(incoming.currentProgress) && !Number.isNaN(current.currentProgress)) {
      if (incoming.currentProgress < current.currentProgress) return true;
      if (incoming.currentProgress > current.currentProgress) return false;
    }

    return false;
  }

  parseJobProgress(value) {
    const parsed = Number.parseInt(value, 10);
    return Number.isNaN(parsed) ? Number.NaN : parsed;
  }

  jobTrackingStateRank(state) {
    const ranks = { pending: 0, running: 1, failure: 2, success: 3, expired: 4 };
    return ranks[state] ?? -1;
  }
}
